import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Table, Button, Input, Space, Popconfirm, message, Form } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import apiClient from "../../../../api/axios";

const AircraftMappingDrawer = ({ open, onClose }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const fetchMappings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.get("/v1/settlement/aircraft-mappings");
            setData(res.data || []);
        } catch (err) {
            message.error(`加载失败: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) fetchMappings();
    }, [open, fetchMappings]);

    const handleAdd = () => {
        if (editingId !== null) return;
        const newRow = { id: "new", excel_identifier: "", aircraft_reg: "", remark: "", _isNew: true };
        setData((prev) => [newRow, ...prev]);
        setEditingId("new");
        form.setFieldsValue({ excel_identifier: "", aircraft_reg: "", remark: "" });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingId === "new") {
                await apiClient.post("/v1/settlement/aircraft-mappings", values);
                message.success("新增成功");
            } else {
                await apiClient.put(`/v1/settlement/aircraft-mappings/${editingId}`, values);
                message.success("更新成功");
            }
            setEditingId(null);
            fetchMappings();
        } catch (err) {
            if (err.response?.data?.detail) {
                message.error(err.response.data.detail);
            } else if (!err.errorFields) {
                message.error(`保存失败: ${err.message}`);
            }
        }
    };

    const handleCancel = () => {
        if (editingId === "new") {
            setData((prev) => prev.filter((r) => r.id !== "new"));
        }
        setEditingId(null);
    };

    const handleDelete = async (id) => {
        try {
            await apiClient.delete(`/v1/settlement/aircraft-mappings/${id}`);
            message.success("已删除");
            fetchMappings();
        } catch (err) {
            message.error(`删除失败: ${err.message}`);
        }
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({
            excel_identifier: record.excel_identifier,
            aircraft_reg: record.aircraft_reg,
            remark: record.remark || "",
        });
    };

    const columns = [
        {
            title: "Excel 飞机标识",
            dataIndex: "excel_identifier",
            width: 160,
            render: (v, rec) =>
                editingId === rec.id ? (
                    <Form.Item name="excel_identifier" rules={[{ required: true, message: "必填" }]} style={{ margin: 0 }}>
                        <Input size="small" placeholder="如 1234 或 B1234" />
                    </Form.Item>
                ) : v,
        },
        {
            title: "航班监控注册号",
            dataIndex: "aircraft_reg",
            width: 140,
            render: (v, rec) =>
                editingId === rec.id ? (
                    <Form.Item name="aircraft_reg" rules={[{ required: true, message: "必填" }]} style={{ margin: 0 }}>
                        <Input size="small" placeholder="如 B-1234" />
                    </Form.Item>
                ) : v,
        },
        {
            title: "备注",
            dataIndex: "remark",
            render: (v, rec) =>
                editingId === rec.id ? (
                    <Form.Item name="remark" style={{ margin: 0 }}>
                        <Input size="small" placeholder="选填" />
                    </Form.Item>
                ) : (v || "-"),
        },
        {
            title: "操作",
            width: 120,
            render: (_, rec) => {
                if (editingId === rec.id) {
                    return (
                        <Space size="small">
                            <Button type="link" size="small" icon={<SaveOutlined />} onClick={handleSave}>保存</Button>
                            <Button type="link" size="small" icon={<CloseOutlined />} onClick={handleCancel}>取消</Button>
                        </Space>
                    );
                }
                if (editingId !== null) return null;
                return (
                    <Space size="small">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(rec)} />
                        <Popconfirm title="确认删除？" onConfirm={() => handleDelete(rec.id)} okText="删除" cancelText="取消">
                            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <Drawer
            title="飞机号对照清单"
            open={open}
            onClose={onClose}
            width={640}
            extra={
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAdd} disabled={editingId !== null}>
                    新增映射
                </Button>
            }
        >
            <Form form={form} component={false}>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    size="small"
                    pagination={false}
                />
            </Form>
        </Drawer>
    );
};

export default AircraftMappingDrawer;
